import { Handshake, Loader2, MessageSquarePlus } from "lucide-react";
import { useShowContext, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { Project } from "../types";
import { LogActivityDialog } from "./DealJournal";
import { useProjectDeal, useProjectPeople } from "./DealFollowUp";
import { useOfferOpen } from "./OfferPage";
import { useInvited } from "./useInvited";

/** La suite attendue d'une fiche, le suivi de l'affaire, toujours à portée en bas du panneau ou de la page.
 * La recherche de contacts se lance depuis la section des contacts. */
export const ProjectActions = ({ className }: { className?: string }) => {
  const { record } = useShowContext<Project>();
  if (!record) return null;
  return <Actions project={record} className={className} />;
};

const Actions = ({
  project,
  className,
}: {
  project: Project;
  className?: string;
}) => {
  const translate = useTranslate();
  const offerOpen = useOfferOpen();
  const { deal, busy: following, follow } = useProjectDeal(project);
  const invited = useInvited();
  const { data: people } = useProjectPeople(project.id);
  const [logOpen, setLogOpen] = useState(false);
  const canLog = !!deal && !invited && !deal.archived_at;
  return (
    <div
      role="group"
      aria-label={translate("resources.projects.actions.label")}
      className={cn(
        "flex shrink-0 gap-2 border-t border-border bg-card px-4 py-3",
        className,
      )}
    >
      {deal ? (
        <Button className="min-w-0 flex-1" asChild>
          <Link to={`/deals/${deal.id}/show`}>
            <Handshake />
            <span className="truncate">
              {translate("resources.deals.follow_up.open_deal")}
            </span>
          </Link>
        </Button>
      ) : invited ? null : (
        <Button
          className="min-w-0 flex-1"
          disabled={following}
          onClick={follow}
        >
          {following ? <Loader2 className="animate-spin" /> : <Handshake />}
          <span className="truncate">
            {translate("resources.deals.follow_up.follow")}
          </span>
        </Button>
      )}
      {/* Téléphone : noter un échange sans quitter la barre (le journal de la fiche a son propre bouton). */}
      {canLog && (
        <>
          <Button
            variant="outline"
            className="shrink-0 md:hidden"
            onClick={() => setLogOpen(true)}
          >
            <MessageSquarePlus />
            <span className="truncate">
              {translate("resources.deals.journal.log")}
            </span>
          </Button>
          <LogActivityDialog
            deal={deal}
            contacts={people ?? []}
            open={logOpen}
            onOpenChange={setLogOpen}
            onLogged={() => {}}
          />
        </>
      )}
      {/* Avec une affaire transmise, le motif de l'abonnement est dans le bloc des contacts (un seul par écran). */}
      {invited && !deal ? (
        <p className="min-w-0 flex-1 self-center text-xs text-muted-foreground">
          {translate("resources.projects.actions.invited")}{" "}
          {offerOpen && (
            <Link to="/offer" className="font-medium text-foreground underline">
              {translate("resources.projects.actions.see_offer")}
            </Link>
          )}
        </p>
      ) : null}
    </div>
  );
};
